/**
 * AtomPanel — UI for the AtomSource modulation tab.
 *
 * Shows a periodic-table grid for element selection, the current
 * element's shell breakdown with a "now-playing" highlight, plus
 * controls for root note, scale, mode, rate, trigger mode, and a
 * Play Chord button that fires the element's shells into the synth's
 * voices.
 *
 * Three trigger behaviors (selected via the Trigger dropdown):
 *   - 'off'   — keyboard plays single notes as normal.
 *   - 'chord' — keyboard plays the element's whole shell-chord
 *               transposed by the pressed key. Hold = sustain, release
 *               = release.
 *   - 'arp'   — keyboard cycles shells one at a time at the source's
 *               `rate`. Hold = continue arping, release = stop.
 *
 * `playAt(midi, vel)` and `releaseAt(midi)` are the entry points
 * main.ts wires from the keyboard's noteOn/noteOff callbacks.
 */

import { ELEMENTS, ATOM_SCALE_NAMES, type Element } from '@ac-slayer/synth-core';
import type { AtomSource } from '@ac-slayer/synth-core';

export interface AtomPanelOptions {
  readonly source: AtomSource;
  /** Fire a single note into the synth's voices. AtomPanel calls
   *  this once per shell-note in chord mode, once per arp step in
   *  arp mode, and once per note in the Play-Chord one-shot. */
  readonly onNoteOn: (midi: number, velocity: number) => void;
  /** Mirror of onNoteOn for releases. */
  readonly onNoteOff: (midi: number) => void;
}

interface HeldKeyState {
  /** Mode this key was pressed in — frozen at press time so a
   *  mid-hold trigger swap doesn't get the held key into a confusing
   *  half-state. */
  mode: 'chord' | 'arp';
  /** Every shell-note currently sounding under this held key. In
   *  chord mode this is every transposed shell; in arp mode it's at
   *  most the single note currently fired. */
  held: number[];
  /** The shell-notes computed at noteOn time. We freeze them at
   *  press time so a mid-hold element change doesn't switch the
   *  user mid-arp across a different chord. */
  shellNotes: number[];
  /** Velocity captured at press, reused on each arp step. */
  velocity: number;
}

export class AtomPanel {
  readonly element: HTMLElement;
  private readonly source: AtomSource;
  private readonly onNoteOn: (midi: number, velocity: number) => void;
  private readonly onNoteOff: (midi: number) => void;

  private readonly grid: HTMLElement;
  private readonly elementCells = new Map<string, HTMLButtonElement>();
  private readonly infoEl: HTMLElement;
  private readonly shellsEl: HTMLElement;
  private readonly chordPreview: HTMLElement;

  private readonly elementSelect: HTMLSelectElement;
  private readonly rootInput: HTMLInputElement;
  private readonly scaleSelect: HTMLSelectElement;
  private readonly rateInput: HTMLInputElement;
  private readonly syncSelect: HTMLSelectElement;
  private readonly modeSelect: HTMLSelectElement;
  private readonly triggerButtons: Map<'off' | 'chord' | 'arp', HTMLButtonElement> = new Map();
  private readonly playBtn: HTMLButtonElement;

  private readonly unsubscribers: Array<() => void> = [];

  /** Currently-held keyboard keys keyed by their pressed MIDI note. */
  private readonly heldKeys = new Map<number, HeldKeyState>();
  /** Last source step we observed. Used to detect tick events vs.
   *  parameter-change emits — only ticks should advance arp notes. */
  private lastObservedStep = -1;
  /** Notes auto-released by the Play Chord button — separate map so
   *  a held keyboard chord doesn't accidentally release the one-shot. */
  private playChordTimer: ReturnType<typeof setTimeout> | null = null;
  private playChordNotes: number[] = [];

  constructor(opts: AtomPanelOptions) {
    this.source = opts.source;
    this.onNoteOn = opts.onNoteOn;
    this.onNoteOff = opts.onNoteOff;

    this.element = document.createElement('div');
    this.element.className = 'atom-panel';

    // ── Header ──────────────────────────────────────────────────────
    const header = document.createElement('div');
    header.className = 'atom-header';
    this.infoEl = document.createElement('div');
    this.infoEl.className = 'atom-info';
    header.appendChild(this.infoEl);
    this.element.appendChild(header);

    // ── Body: 2-column layout. ─────────────────────────────────────
    const body = document.createElement('div');
    body.className = 'atom-body';

    this.grid = document.createElement('div');
    this.grid.className = 'atom-grid';
    this.buildGrid();
    body.appendChild(this.grid);

    const side = document.createElement('div');
    side.className = 'atom-side';

    // Shells display.
    const shellsWrap = document.createElement('div');
    shellsWrap.className = 'atom-shells-wrap';
    const shellsLabel = document.createElement('div');
    shellsLabel.className = 'atom-side-label';
    shellsLabel.textContent = 'Shells';
    this.shellsEl = document.createElement('div');
    this.shellsEl.className = 'atom-shells';
    shellsWrap.append(shellsLabel, this.shellsEl);
    side.appendChild(shellsWrap);

    // Chord preview.
    const chordWrap = document.createElement('div');
    chordWrap.className = 'atom-chord-wrap';
    const chordLabel = document.createElement('div');
    chordLabel.className = 'atom-side-label';
    chordLabel.textContent = 'Chord';
    this.chordPreview = document.createElement('div');
    this.chordPreview.className = 'atom-chord';
    chordWrap.append(chordLabel, this.chordPreview);
    side.appendChild(chordWrap);

    // Controls grid.
    const controls = document.createElement('div');
    controls.className = 'atom-controls';

    // Element dropdown — duplicates the grid for keyboard accessibility.
    this.elementSelect = makeSelect();
    for (const el of ELEMENTS) {
      const opt = document.createElement('option');
      opt.value = el.symbol;
      opt.textContent = `${el.atomicNumber}. ${el.symbol} — ${el.name}`;
      this.elementSelect.appendChild(opt);
    }
    this.elementSelect.addEventListener('change', () => {
      this.source.element.set(this.elementSelect.value, 'ui');
    });
    controls.appendChild(makeField('Element', this.elementSelect));

    // Root MIDI as a number input.
    this.rootInput = document.createElement('input');
    this.rootInput.type = 'number';
    this.rootInput.min = '24';
    this.rootInput.max = '96';
    this.rootInput.step = '1';
    this.rootInput.className = 'atom-input';
    this.rootInput.addEventListener('change', () => {
      const n = Number.parseInt(this.rootInput.value, 10);
      if (Number.isFinite(n)) this.source.root.set(n, 'ui');
    });
    controls.appendChild(makeField('Root (MIDI)', this.rootInput));

    // Scale dropdown.
    this.scaleSelect = makeSelect();
    for (const name of ATOM_SCALE_NAMES) {
      const opt = document.createElement('option');
      opt.value = name;
      opt.textContent = name;
      this.scaleSelect.appendChild(opt);
    }
    this.scaleSelect.addEventListener('change', () => {
      this.source.scale.set(this.scaleSelect.value as never, 'ui');
    });
    controls.appendChild(makeField('Scale', this.scaleSelect));

    // Rate as Hz input — used when Sync is 'free'.
    this.rateInput = document.createElement('input');
    this.rateInput.type = 'number';
    this.rateInput.min = '0.1';
    this.rateInput.max = '30';
    this.rateInput.step = '0.1';
    this.rateInput.className = 'atom-input';
    this.rateInput.addEventListener('change', () => {
      const n = Number.parseFloat(this.rateInput.value);
      if (Number.isFinite(n)) this.source.rate.set(n, 'ui');
    });
    controls.appendChild(makeField('Rate (Hz)', this.rateInput));

    // Sync — 'free' uses the Rate input; any musical division steps
    // at that fraction of the transport's BPM. Same options as
    // LFO/S&H/Sequencer so users have consistent muscle memory.
    this.syncSelect = makeSelect();
    for (const opt of this.source.sync.options) {
      const o = document.createElement('option');
      o.value = opt;
      o.textContent = opt;
      this.syncSelect.appendChild(o);
    }
    this.syncSelect.addEventListener('change', () => {
      this.source.sync.set(this.syncSelect.value as never, 'ui');
    });
    controls.appendChild(makeField('Sync', this.syncSelect));

    // Mode (raw) — proper select. Two options: scale-quantized or raw.
    this.modeSelect = makeSelect();
    for (const [value, label] of [['off', 'scale'], ['on', 'raw']] as const) {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = label;
      this.modeSelect.appendChild(opt);
    }
    this.modeSelect.addEventListener('change', () => {
      this.source.raw.set(this.modeSelect.value as 'off' | 'on', 'ui');
    });
    controls.appendChild(makeField('Mode', this.modeSelect));

    // Trigger — segmented buttons rather than a select so the user
    // sees all three options at once and clicking a different one
    // is a single tap (vs. open-dropdown → click). Releases all held
    // notes on every change since the semantics of "held" shift
    // between modes.
    const triggerGroup = document.createElement('div');
    triggerGroup.className = 'atom-trigger-group';
    triggerGroup.setAttribute('role', 'group');
    for (const value of ['off', 'chord', 'arp'] as const) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'atom-trigger-btn';
      btn.textContent = value;
      btn.addEventListener('click', () => {
        if (this.source.trigger.value === value) return;
        this.releaseAllHeldKeys();
        this.source.trigger.set(value, 'ui');
      });
      triggerGroup.appendChild(btn);
      this.triggerButtons.set(value, btn);
    }
    controls.appendChild(makeField('Trigger', triggerGroup));

    side.appendChild(controls);

    // Play Chord button.
    this.playBtn = document.createElement('button');
    this.playBtn.type = 'button';
    this.playBtn.className = 'btn btn-primary atom-play-btn';
    this.playBtn.textContent = '▶ Play element chord';
    this.playBtn.addEventListener('click', () => this.playOneShot());
    side.appendChild(this.playBtn);

    body.appendChild(side);
    this.element.appendChild(body);

    // Subscriptions + initial render. The source emits on every
    // tick AND on parameter changes — onSourceUpdate diffs the
    // step index so arp note-fires only happen on real ticks while
    // the panel still re-renders on every emit.
    this.unsubscribers.push(this.source.subscribe(() => this.onSourceUpdate()));
    this.lastObservedStep = this.source.getCurrentStepIndex();
    this.render();
  }

  dispose(): void {
    this.releaseAllHeldKeys();
    if (this.playChordTimer) {
      clearTimeout(this.playChordTimer);
      this.playChordTimer = null;
    }
    for (const u of this.unsubscribers) u();
    this.unsubscribers.length = 0;
    this.element.remove();
  }

  // ── Public: keyboard trigger entry points ────────────────────────

  /** Whether the panel is currently intercepting keyboard input.
   *  main.ts checks this on every keyboard noteOn — `false` means
   *  "let the normal voice path through". */
  isTriggerActive(): boolean {
    return this.source.trigger.value !== 'off';
  }

  /**
   * Begin playing the element's chord transposed so its root sits
   * at `pressedMidi`. In chord mode all shells fire simultaneously;
   * in arp mode they cycle one at a time at the source's rate.
   * Idempotent — pressing the same key twice without an intervening
   * releaseAt is a no-op.
   */
  playAt(pressedMidi: number, velocity: number): void {
    const trigger = this.source.trigger.value;
    if (trigger === 'off') return;
    if (this.heldKeys.has(pressedMidi)) return;

    const elementRoot = this.source.root.value;
    const baseShells = this.source.toChordNotes();
    if (baseShells.length === 0) return;
    const offset = Math.round(pressedMidi - elementRoot);
    // PRESERVE duplicates here. The arp uses the source's step index
    // (which cycles across the element's full shell count, including
    // duplicates) to look up notes; deduping shellNotes would shrink
    // the list and the modulo lookup would scramble the mapping —
    // shell 18 in position 4 would play whatever note happened to
    // sit at the deduped tail. We only dedupe inside the chord-mode
    // noteOn loop below, where the issue is voice-allocation
    // collisions, not step-to-note alignment.
    const shellNotes = baseShells.map((n) => clampMidi(n + offset));

    const state: HeldKeyState = {
      mode: trigger,
      held: [],
      shellNotes,
      velocity,
    };
    this.heldKeys.set(pressedMidi, state);

    if (trigger === 'chord') {
      // Dedupe just for the simultaneous-noteOn pass — VoiceManager
      // overwrites its noteToVoices entry on duplicate noteOns and
      // orphans the first allocation's voices, which then ring
      // forever. The held list tracks the deduped set so each
      // noteOn gets exactly one matching noteOff.
      for (const n of new Set(shellNotes)) {
        this.onNoteOn(n, velocity);
        state.held.push(n);
      }
      return;
    }

    // 'arp' — fire shellNotes[currentSourceStep] immediately so the
    // user gets instant feedback AND the audio lines up with the
    // shells visualization (which is showing that step). Subsequent
    // notes fire on the source's tick via onSourceUpdate(), which
    // drives both the visualization and every held arp key's
    // step-advance from a single shared clock. No per-key timer.
    const step = this.source.getCurrentStepIndex();
    const note = shellNotes[step % shellNotes.length];
    if (note !== undefined) {
      this.onNoteOn(note, velocity);
      state.held.push(note);
    }
  }

  /** Release whatever the corresponding playAt fired. Iterates the
   *  full held list so duplicate-MIDI entries (chord-mode quantization
   *  collisions) get one noteOff per noteOn — anything less leaks
   *  voices in poly mode. */
  releaseAt(pressedMidi: number): void {
    const state = this.heldKeys.get(pressedMidi);
    if (!state) return;
    for (const n of state.held) this.onNoteOff(n);
    state.held.length = 0;
    this.heldKeys.delete(pressedMidi);
  }

  /** Hard-cut every held trigger. Used when changing trigger mode
   *  or disposing the panel — anything dangling would leak voices. */
  private releaseAllHeldKeys(): void {
    for (const midi of [...this.heldKeys.keys()]) this.releaseAt(midi);
  }

  // ── Internal: source-tick handler ────────────────────────────────

  /**
   * Called from the source.subscribe() listener on every emit. Two
   * jobs:
   *
   *   1. Detect whether the source's stepIndex actually advanced —
   *      param changes (element/scale/raw/root/etc) also trigger
   *      emits but don't move the cursor. We diff against the last
   *      observed step so arp note-fires are gated to real ticks.
   *
   *   2. For each held arp-mode key, release the previous step's
   *      note and fire the new step's note transposed to that key.
   *      The shells visualization (driven by the same source step)
   *      and the arp's audio output stay in lockstep because they
   *      share one clock.
   *
   * Chord-mode held keys ignore tick events — they hold their full
   * chord steady and only respond to release.
   */
  private onSourceUpdate(): void {
    this.render();
    const step = this.source.getCurrentStepIndex();
    if (step === this.lastObservedStep) return;
    this.lastObservedStep = step;

    if (this.heldKeys.size === 0) return;
    for (const state of this.heldKeys.values()) {
      if (state.mode !== 'arp' || state.shellNotes.length === 0) continue;
      // Release whatever was previously firing under this key.
      for (const n of state.held) this.onNoteOff(n);
      state.held.length = 0;
      // Fire the new step's note (transposed shellNote at this index).
      const note = state.shellNotes[step % state.shellNotes.length];
      if (note !== undefined) {
        this.onNoteOn(note, state.velocity);
        state.held.push(note);
      }
    }
  }

  // ── Internal: Play Chord one-shot ───────────────────────────────

  private playOneShot(): void {
    // Dedupe to sidestep the same VoiceManager-overwrites-on-duplicate
    // issue described in `playAt`. Without this, a chord with two
    // shells that quantize to the same note leaks the older voice
    // every time the button is pressed.
    const notes = [...new Set(this.source.toChordNotes())];
    if (notes.length === 0) return;
    if (this.playChordTimer) {
      clearTimeout(this.playChordTimer);
      for (const n of this.playChordNotes) this.onNoteOff(n);
    }
    for (const n of notes) this.onNoteOn(n, 0.85);
    this.playChordNotes = [...notes];
    this.playChordTimer = setTimeout(() => {
      for (const n of this.playChordNotes) this.onNoteOff(n);
      this.playChordNotes = [];
      this.playChordTimer = null;
    }, 1500);
  }

  // ── Grid construction ────────────────────────────────────────────

  private buildGrid(): void {
    this.grid.innerHTML = '';
    this.elementCells.clear();
    for (const el of ELEMENTS) {
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = `atom-cell cat-${el.category}`;
      cell.style.gridColumnStart = String(el.group);
      cell.style.gridRowStart = String(el.period);
      cell.title = `${el.atomicNumber}. ${el.name} — shells [${el.shells.join(', ')}]`;
      cell.dataset['symbol'] = el.symbol;
      cell.innerHTML = `
        <span class="atom-cell-num">${el.atomicNumber}</span>
        <span class="atom-cell-symbol">${el.symbol}</span>
      `;
      cell.addEventListener('click', () => {
        this.source.element.set(el.symbol, 'ui');
      });
      this.grid.appendChild(cell);
      this.elementCells.set(el.symbol, cell);
    }
  }

  // ── Render pipeline ──────────────────────────────────────────────

  private render(): void {
    const el = this.source.getElement();
    this.renderHeader(el);
    this.renderGridSelection(el);
    this.renderShells(el);
    this.renderChord();
    this.renderControls();
  }

  private renderHeader(el: Element): void {
    this.infoEl.innerHTML = `
      <span class="atom-symbol cat-${el.category}">${el.symbol}</span>
      <div class="atom-info-meta">
        <div class="atom-info-name">${el.name}</div>
        <div class="atom-info-num">Z = ${el.atomicNumber}  ·  ${el.category.replace(/-/g, ' ')}</div>
      </div>
    `;
  }

  private renderGridSelection(el: Element): void {
    for (const [sym, cell] of this.elementCells) {
      cell.classList.toggle('selected', sym === el.symbol);
    }
  }

  private renderShells(el: Element): void {
    const cur = this.source.getCurrentStepIndex();
    this.shellsEl.innerHTML = '';
    el.shells.forEach((count, idx) => {
      const cell = document.createElement('div');
      cell.className = 'atom-shell-cell';
      if (idx === cur) cell.classList.add('playing');
      cell.innerHTML = `
        <span class="atom-shell-idx">${idx + 1}</span>
        <span class="atom-shell-count">${count}</span>
      `;
      this.shellsEl.appendChild(cell);
    });
  }

  private renderChord(): void {
    // Dedupe so the display matches what the audio actually plays.
    // Quantized chord-mapping routinely produces duplicates (multiple
    // shells mapping to the same scale-degree+octave, or MIDI clamping
    // pinning out-of-range values onto 127); the audio path sees the
    // unique set, and showing the raw set in the display made it look
    // like "different chips, same sound" because duplicate chips
    // showed the same note name twice.
    const notes = [...new Set(this.source.toChordNotes())].sort((a, b) => a - b);
    this.chordPreview.innerHTML = '';
    notes.forEach((midi) => {
      const chip = document.createElement('span');
      chip.className = 'atom-chord-note';
      chip.textContent = midiToNoteName(midi);
      this.chordPreview.appendChild(chip);
    });
  }

  private renderControls(): void {
    this.elementSelect.value = this.source.element.value;
    this.rootInput.value = String(Math.round(this.source.root.value));
    this.scaleSelect.value = this.source.scale.value;
    this.rateInput.value = this.source.rate.value.toFixed(2);
    this.syncSelect.value = this.source.sync.value;
    // When sync is locked to a division, Rate has no effect — disable
    // the input so the UI hints at the actual control surface.
    this.rateInput.disabled = this.source.sync.value !== 'free';
    this.modeSelect.value = this.source.raw.value;
    const trigger = this.source.trigger.value;
    for (const [value, btn] of this.triggerButtons) {
      btn.classList.toggle('active', value === trigger);
    }
  }
}

// ── Tiny helpers ────────────────────────────────────────────────────

function makeField(label: string, control: HTMLElement): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'atom-field';
  const lbl = document.createElement('span');
  lbl.className = 'atom-field-label';
  lbl.textContent = label;
  wrap.append(lbl, control);
  return wrap;
}

function makeSelect(): HTMLSelectElement {
  const sel = document.createElement('select');
  sel.className = 'atom-select';
  return sel;
}

function midiToNoteName(midi: number): string {
  const NAMES = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
  const n = NAMES[midi % 12] ?? '?';
  const octave = Math.floor(midi / 12) - 1;
  return `${n}${octave}`;
}

function clampMidi(n: number): number {
  return Math.max(0, Math.min(127, Math.round(n)));
}
